import { Box, Button, CircularProgress, Typography } from "@mui/material";
import PictureAsPdfOutlinedIcon from "@mui/icons-material/PictureAsPdfOutlined";
import { useState } from "react";
import type { SponsorCampaign } from "../campaignTypes";
import { openPartnerReportPdf } from "../buildPartnerReportHtml";
import {
  PORTAL_MAX_WIDTH,
  bodySx,
  cardSurfaceSx,
  eyebrowSx,
  portalFont,
  sectionTitleSx,
} from "../partnerTheme";

export default function ReportPdfDownload({ campaign }: { campaign: SponsorCampaign }) {
  const [busy, setBusy] = useState(false);
  const { assets } = campaign;

  function handleDownload() {
    setBusy(true);
    try {
      openPartnerReportPdf(campaign);
    } finally {
      window.setTimeout(() => setBusy(false), 800);
    }
  }

  return (
    <Box
      id="assets"
      component="section"
      sx={{
        maxWidth: PORTAL_MAX_WIDTH,
        mx: "auto",
        px: { xs: 2, md: 4 },
        py: { xs: 6, md: 10 },
        scrollMarginTop: 96,
      }}
    >
      {assets.eyebrow ? <Typography sx={eyebrowSx}>{assets.eyebrow}</Typography> : null}
      <Typography sx={sectionTitleSx}>{assets.title}</Typography>
      {assets.description ? (
        <Typography sx={{ ...bodySx, maxWidth: 640, mb: 4, color: "var(--portal-text)" }}>
          {assets.description}
        </Typography>
      ) : null}

      <Box
        sx={{
          ...cardSurfaceSx,
          borderRadius: "24px",
          p: { xs: 3, md: 4 },
          display: "flex",
          flexDirection: { xs: "column", sm: "row" },
          alignItems: { xs: "stretch", sm: "center" },
          justifyContent: "space-between",
          gap: 2.5,
          maxWidth: 720,
        }}
      >
        <Box sx={{ display: "flex", gap: 2, alignItems: "flex-start", minWidth: 0 }}>
          <PictureAsPdfOutlinedIcon sx={{ color: "var(--portal-accent)", mt: 0.25 }} />
          <Box>
            <Typography sx={{ fontFamily: portalFont, fontWeight: 700, color: "var(--portal-text)", mb: 0.5 }}>
              {campaign.campaignName}
            </Typography>
            <Typography sx={{ ...bodySx, fontSize: "0.9rem", mb: 0 }}>
              Same results, formatted for print. In the dialog, choose Save as PDF.
            </Typography>
          </Box>
        </Box>
        <Button
          onClick={handleDownload}
          disabled={busy}
          sx={{
            flexShrink: 0,
            textTransform: "none",
            fontFamily: portalFont,
            fontWeight: 700,
            borderRadius: "999px",
            px: 2.75,
            py: 1.25,
            bgcolor: "var(--portal-accent)",
            color: "#fff",
            "&:hover": { bgcolor: "var(--portal-accent)", filter: "brightness(1.08)" },
            "&.Mui-disabled": { color: "rgba(255,255,255,0.7)" },
          }}
        >
          {busy ? <CircularProgress size={20} sx={{ color: "#fff" }} /> : "Download PDF"}
        </Button>
      </Box>
    </Box>
  );
}
